import { z } from 'zod';
import { LIMITS } from '../limits.js';

/**
 * Reports ("Жалобы") — a user flagging a message or another user for
 * moderation. Required for App Store / Google Play (a way to report
 * objectionable content + the people posting it).
 *
 * Wire shapes only. The server owns ALL routing and visibility:
 *   - it derives `scope` (guild / direct / profile), `guildId`, `channelId`,
 *     the reported author and the content snapshot from the target id, so a
 *     forged guildId/channelId can't smuggle a report onto something the
 *     reporter can't see;
 *   - it decides which queue the report lands in (server moderators vs. the
 *     platform queue) and whether a critical category escalates to platform.
 * The client only ever names WHAT it is reporting and WHY.
 */

/** What is being reported. */
export const reportKindSchema = z.enum(['message', 'user']);
export type ReportKind = z.infer<typeof reportKindSchema>;

/**
 * Why. `csam` / `illegal` / `violence` are the "critical" categories the
 * server always escalates to the platform queue, even for in-server reports —
 * a community owner must not be able to bury platform-level abuse.
 */
export const reportReasonSchema = z.enum([
  'spam',
  'harassment',
  'nsfw',
  'violence',
  'self_harm',
  'csam',
  'illegal',
  'other',
]);
export type ReportReason = z.infer<typeof reportReasonSchema>;

/** Where the reported thing lives — drives which moderators see it. */
export const reportScopeSchema = z.enum(['guild', 'direct', 'profile']);
export type ReportScope = z.infer<typeof reportScopeSchema>;

/** Moderation lifecycle. */
export const reportStatusSchema = z.enum(['open', 'reviewing', 'actioned', 'dismissed']);
export type ReportStatus = z.infer<typeof reportStatusSchema>;

/**
 * Create a report. Exactly one target id is required, matching `kind`:
 *   - kind 'message' → `messageId`
 *   - kind 'user'    → `userId`
 */
export const createReportSchema = z
  .discriminatedUnion('kind', [
    z.object({
      kind: z.literal('message'),
      messageId: z.string().cuid(),
      reason: reportReasonSchema,
      note: z.string().trim().max(LIMITS.report.noteMax).optional(),
    }),
    z.object({
      kind: z.literal('user'),
      userId: z.string().cuid(),
      reason: reportReasonSchema,
      note: z.string().trim().max(LIMITS.report.noteMax).optional(),
    }),
  ]);
export type CreateReportInput = z.infer<typeof createReportSchema>;

/** A moderator resolving a report. `status` flips it out of the open queue. */
export const resolveReportSchema = z
  .object({
    status: z.enum(['reviewing', 'actioned', 'dismissed']),
    resolution: z.string().trim().max(LIMITS.report.resolutionMax).optional(),
  });
export type ResolveReportInput = z.infer<typeof resolveReportSchema>;

/** Queue filter: which moderation surface + status the caller wants. */
export const reportQueueQuerySchema = z.object({
  /** 'guild' needs `guildId`; 'platform' is the global out-of-server queue. */
  surface: z.enum(['guild', 'platform']),
  guildId: z.string().cuid().optional(),
  status: reportStatusSchema.optional(),
  cursor: z.string().cuid().optional(),
});
export type ReportQueueQuery = z.infer<typeof reportQueueQuerySchema>;

// ── Response shapes (what the server returns) ──────────────────────────────

export interface ReportPublicUser {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

/** One report as shown in a moderation queue. */
export interface ReportItem {
  id: string;
  kind: ReportKind;
  scope: ReportScope;
  reason: ReportReason;
  note: string | null;
  status: ReportStatus;
  platformEscalated: boolean;
  snapshot: unknown;
  resolution: string | null;
  createdAt: string;
  resolvedAt: string | null;
  guildId: string | null;
  channelId: string | null;
  messageId: string | null;
  reporter: ReportPublicUser | null;
  targetUser: ReportPublicUser | null;
  reviewer: ReportPublicUser | null;
  guild: { id: string; name: string } | null;
}

export interface ReportQueueResponse {
  reports: ReportItem[];
  nextCursor: string | null;
}

/** What moderation surfaces the current user may open. */
export interface ReportAccess {
  /** May open the platform queue (root admin or assigned moderator). */
  platformAdmin: boolean;
  /** May manage the moderation team (root admin only). */
  rootAdmin: boolean;
}

export interface ModeratorEntry {
  user: ReportPublicUser;
  addedBy: ReportPublicUser | null;
  createdAt: string;
}

export interface ModeratorTeamResponse {
  /** Env-defined root admins (read-only — managed in config, not the UI). */
  rootAdminIds: string[];
  /** The root admins that exist as users, for showing a name and avatar.
   *  Optional: an older server sends only the ids. */
  rootAdmins?: ReportPublicUser[];
  moderators: ModeratorEntry[];
}
